import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  SmartToy as BotIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { botService } from '../services/api';

const Bots: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery('bots', async () => {
    const response = await botService.list();
    return response.data;
  });

  const toggleBotMutation = useMutation(
    ({ id, isActive }: { id: string; isActive: boolean }) =>
      botService.toggle(id, isActive),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bots');
      },
    }
  );

  const deleteBotMutation = useMutation(
    (id: string) => botService.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('bots');
      },
    }
  );

  const handleToggleBot = async (bot: any) => {
    // Se está tentando ativar e não tem WhatsApp conectado
    if (!bot.is_active && !bot.phone_number) {
      alert('Você precisa conectar um WhatsApp antes de ativar o bot.');
      return;
    }
    
    try {
      await toggleBotMutation.mutateAsync({
        id: bot.id,
        isActive: !bot.is_active,
      });
    } catch (error: any) {
      console.error('Erro ao alterar status do bot:', error);
      // Mostrar erro do servidor se houver
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      }
    }
  };

  const handleDeleteBot = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este bot?')) {
      try {
        await deleteBotMutation.mutateAsync(id);
      } catch (error) {
        console.error('Erro ao excluir bot:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <Container>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={6} lg={4} key={i}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error">Erro ao carregar bots</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Meus Bots</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/bots/new')}
        >
          Criar Novo Bot
        </Button>
      </Box>

      {data?.bots?.length === 0 ? (
        <Box textAlign="center" py={8}>
          <BotIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Nenhum bot encontrado
          </Typography>
          <Typography variant="body1" color="textSecondary" mb={3}>
            Crie seu primeiro bot para começar a automatizar conversas no WhatsApp
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/bots/new')}
          >
            Criar Bot
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {data?.bots?.map((bot: any) => (
            <Grid item xs={12} md={6} lg={4} key={bot.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {bot.name}
                      </Typography>
                      <Box display="flex" gap={1} mb={1}>
                        {bot.is_active ? (
                          <Chip label="Ativo" color="success" size="small" />
                        ) : (
                          <Chip label="Inativo" size="small" />
                        )}
                        {bot.phone_number && (
                          <Chip
                            icon={<WhatsAppIcon />}
                            label="Conectado"
                            color="primary"
                            size="small"
                          />
                        )}
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/bots/${bot.id}`)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary">
                    Chave OpenAI: {bot.openai_key}
                  </Typography>
                  {bot.assistant_name && (
                    <Typography variant="body2" color="textSecondary">
                      Assistente: {bot.assistant_name}
                    </Typography>
                  )}
                  {bot.phone_number && (
                    <Typography variant="body2" color="textSecondary">
                      WhatsApp: {bot.phone_number}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={bot.is_active}
                        onChange={() => handleToggleBot(bot)}
                        disabled={toggleBotMutation.isLoading || (!bot.is_active && !bot.phone_number)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Ativo</Typography>
                        {!bot.phone_number && (
                          <Typography variant="caption" color="error">
                            Conecte ao WhatsApp
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <Box sx={{ flexGrow: 1 }} />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteBot(bot.id)}
                    disabled={deleteBotMutation.isLoading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default Bots;